import { Box, Flex } from '@chakra-ui/react';
import { useState, cloneElement, useEffect } from 'react';
import { FaCloudDownloadAlt, FaBoxes, FaSlidersH } from 'react-icons/fa';
import { primaryThemeGradient, primaryThemeGradientDark } from 'constants/theme';
import { Icon } from 'components/UI';
import CollapsibleHalf from 'components/Configurator/Menus/CollapsibleHalf';
import { ConfiguratorTab } from 'constants/configurator';
import { ActiveParameters, IParams, ISettings } from 'types';
import { checkConditionals } from 'utils/conditionals';
import { getParamInventor } from 'utils/parameters';
import AccessOverlay from 'components/Configurator/Configurator/AccessOverlay';
import { checkIfError } from 'utils/helpers';
import { useFetchProductLink, useFetchProducts } from 'hooks';
import { IConditions, IObject, IParameter, IProductResource, IValueCondition } from '../../../../../server/types';
import SideMenuTabs from './SideMenuTabs';
import SideMenuToggle from './SideMenuToggle';
import ProcessMenu from '../ProcessMenu';
import ProductMenu, { ProductMenuProps } from '../ProductMenu';
import { ProcessMenuProps } from '../ProcessMenu/ProcessMenu';
import OptionsMenu, { OptionsMenuProps } from '../OptionsMenuPreload/OptionsMenuPreload';

type SideMenuProps = {
  theme: ISettings | null;
  productDisplayName: string;
  viewParameters?: IParameter[];
  queryParamsSideMenu: { product: string } | null;
  DownloadMenuIcon: boolean;
  skipDownloading: boolean;
  queryParamsProductMenu: Record<string, never>;
  paramsOptions: IParameter[];
  isEnabled: boolean;
  handleGetItemState(data: { newProducts: IProductResource[] }): void;
  handleUpdateSelectedProductStates(data: {
    selectedProduct: string | number | undefined;
    product: IProductResource;
    currentUpdateViewer: boolean;
    parameterData?: IParameter[];
  }): void;
  handleParameterUpdateStates(data: {
    currentActiveParameters: ActiveParameters;
    sentActiveParameters: ActiveParameters;
    currentProduct: IProductResource;
    currentConfigValid: boolean;
    currentInvalidParams: string[];
    currentProductState: {};
  }): void;
  activeParameters: ActiveParameters;
  paramsProcessMenu: ActiveParameters & IParameter[];
};

type OptionsMenuPropsExclude = Omit<OptionsMenuProps, 'updateParam'>;
type OptionsMenuPropsExclude1 = Omit<OptionsMenuPropsExclude, 'parameterUpdate'>;
type OptionsMenuUpdatedProps = Omit<OptionsMenuPropsExclude1, 'params'>;
type ProcessMenuPropsExclude = Omit<ProcessMenuProps, 'params'>;
type ExcludeQueryParams = Omit<ProductMenuProps, 'queryParams'>;
type ExcludeInitialParams = Omit<ExcludeQueryParams, 'setInitialParams'>;
type ProductMenuUpdatedProps = Omit<ExcludeInitialParams, 'updateSelectedProduct'>;

const SideMenu = ({
  theme,
  productDisplayName,
  queryParamsSideMenu,
  DownloadMenuIcon,
  viewParameters,
  skipDownloading,
  paramsProcessMenu,
  product,
  disabled,
  downloadExportStp,
  downloadExportSat,
  downloadExportDwg,
  downloadExport2dDwg,
  modelDownloadUrl,
  drawingDownloadUrl,
  rfq,
  resultFileJobId,
  selectedProduct,
  resultFileNameRef,
  quote,
  quotePdfUrl,
  modelParameters,
  flc,
  flcUrl,
  bom,
  bomCsvUrl,
  paramsOptions,
  valid,
  loading,
  authUser,
  productState,
  renderSelectsMenu,
  invalidParams,
  updateValue,
  queueJob,
  showDownloadMenuIcon,
  jobsLoading = false,
  errorExisted = false,
  productChanged = false,
  queryParamsProductMenu = {},
  viewerLoading = false,
  products,
  isEnabled,
  handleGetItemState,
  handleUpdateSelectedProductStates,
  handleParameterUpdateStates,
  activeParameters,
}: SideMenuProps & ProcessMenuPropsExclude & OptionsMenuUpdatedProps & ProductMenuUpdatedProps) => {
  const [closed, setClosed] = useState(false);

  const handleToggleSideMenu = () => setClosed((prevClosed: boolean) => !prevClosed);

  const [currentTab, setCurrentTab] = useState<ConfiguratorTab>(ConfiguratorTab.PRODUCTS);
  const [fetchProductLink] = useFetchProductLink();
  const [fetchProducts] = useFetchProducts();

  const getParameterValues = (params: object, task: number) => {
    const paramValues = Object.values(params).reduce(
      (
        acc: Record<string, string>,
        param: {
          name: string;
          value: string;
          type: string;
          values?: { displayName: string; value: string | boolean }[];
          inputType?: 'select' | 'text';
        }
      ) => {
        const { name, value, type, values = [], inputType } = param;

        const processedValue = getParamInventor(value, typeof type === 'string' ? type : '', values);

        const booleanValue = `${processedValue}`;

        const inputValue = inputType !== 'select' ? value : booleanValue;

        return { ...acc, [name]: task === 0 ? booleanValue : inputValue };
      },
      {}
    );

    return paramValues;
  };

  const parameterCheck = (params: object, currentParams: ActiveParameters) =>
    JSON.stringify(params) === JSON.stringify(currentParams);

  const parameterUpdate = async (params: object, updateProduct?: IProductResource | null) => {
    const currentProduct = updateProduct ?? product;

    if (!currentProduct) return;

    const refParams: string[] = [];
    let hasRef = false;
    let hasSelects = false;
    let hasTexts = false;

    const selectParams: string[] = [];
    let currentConfigValid = true;
    const currentInvalidParams: string[] = [];
    const currentActiveParameters: ActiveParameters = {};
    const sentActiveParameters: ActiveParameters = {};

    const paramKeys = currentProduct.parameterList;
    const paramValuesDisplay = getParameterValues(params, 0);
    const paramValuesExpression = getParameterValues(params, 1);

    const paramValuesDisplayLowerCase = Object.entries(paramValuesExpression as IParams).reduce(
      (acc: IParams, [key, value]) => {
        acc[key] = typeof value === 'string' ? value.toLowerCase() : value;
        return acc;
      },
      {}
    );

    let index = 0;

    let conditionsReview: (IConditions | IObject | IValueCondition)[] = [];
    const currentProductState = paramKeys?.reduce(
      (acc, val) =>
        val?.pList?.reduce(
          (innerAcc, key) => {
            const parameter = currentProduct?.parameters?.[+key];
            if (!parameter) {
              return innerAcc;
            }
            const { parameterType, onConditions, valueConditions } = parameter;
            const onConditionsArr = onConditions ? [onConditions] : [];
            const valueConditionsArr = valueConditions ? [valueConditions] : [];
            conditionsReview = [...onConditionsArr, ...valueConditionsArr];
            const add =
              parameterType === 'reference' ||
              parameterType === 'note' ||
              parameter.conditional === undefined ||
              parameter.conditional ||
              !onConditions ||
              checkConditionals(onConditions, paramValuesDisplayLowerCase as IParams);

            if (!add) return innerAcc;
            currentActiveParameters[+key] = {
              ...parameter,
              value: paramValuesDisplay[parameter.name] ?? parameter.value,
            };

            if (parameterType !== 'note') {
              sentActiveParameters[index] = {
                ...parameter,
                value: paramValuesExpression[parameter.name] ?? parameter.value,
              };
            }

            hasRef = parameter.parameterType === 'reference';
            if (hasRef) {
              refParams.push(key as string);
            } else {
              const parameterValid = parameter.valueConditions
                ? checkConditionals(parameter.valueConditions as IConditions, paramValuesDisplayLowerCase as IParams)
                : true;

              if (parameterType !== 'note') {
                sentActiveParameters[index].valid = parameterValid;
              }

              currentActiveParameters[+key].valid = parameterValid;
              if (!parameterValid) {
                currentInvalidParams.push(currentActiveParameters[+key].displayName);
                currentConfigValid = conditionsReview.every((item: IConditions | IObject | IValueCondition) => {
                  const currentParamValues = getParameterValues(currentActiveParameters, 1);

                  return checkConditionals(item as IConditions, currentParamValues);
                });
                currentActiveParameters[+key].valid = currentConfigValid;
              }

              const preloaded = parameter.isPreloaded ?? true;
              hasSelects = parameter.inputType === 'select' && preloaded;
              hasTexts = parameter.inputType !== 'select' || !preloaded;

              if (hasSelects) {
                selectParams.push(key as string);
              }
            }
            index += 1;
            return innerAcc;
          },
          { ...acc, hasTexts, hasSelects, hasRef, selectParams, refParams }
        ),
      {}
    );

    if (!parameterCheck(params, currentActiveParameters)) {
      parameterUpdate(currentActiveParameters ?? {}, currentProduct);
      return;
    }

    const currentStates = {
      currentActiveParameters,
      sentActiveParameters,
      currentProduct,
      currentConfigValid,
      currentInvalidParams,
      currentProductState,
    };

    handleParameterUpdateStates(currentStates);
  };

  const setInitialParams = (initialParameterData: IParameter[] | null, currentProduct: IProductResource | null) => {
    if (!initialParameterData) return;

    const params = initialParameterData.reduce(
      (acc: ActiveParameters, parameter, index) => ({
        ...acc,
        [index]: parameter,
      }),
      {}
    );

    parameterUpdate(params, currentProduct);
  };

  const PostProcessMenu = (
    <ProcessMenu
      params={paramsProcessMenu!}
      modelDownloadUrl={modelDownloadUrl}
      modelParameters={modelParameters}
      drawingDownloadUrl={drawingDownloadUrl}
      downloadExportSat={downloadExportSat}
      downloadExportStp={downloadExportStp}
      downloadExportDwg={downloadExportDwg}
      downloadExport2dDwg={downloadExport2dDwg}
      disabled={disabled}
      product={product}
      rfq={rfq}
      skipDownloading={skipDownloading}
      resultFileJobId={resultFileJobId as string}
      selectedProduct={selectedProduct as string}
      resultFileNameRef={resultFileNameRef as string}
      quote={quote}
      quotePdfUrl={quotePdfUrl as string}
      flc={flc}
      flcUrl={flcUrl}
      bom={bom}
      bomCsvUrl={bomCsvUrl}
    />
  );

  const OptionMenu = (
    <OptionsMenu
      theme={theme as ISettings}
      authUser={authUser}
      params={paramsOptions}
      product={product}
      productState={productState}
      modelDownloadUrl={modelDownloadUrl}
      activeParameters={activeParameters}
      parameterUpdate={parameterUpdate}
      updateValue={updateValue}
      queueJob={queueJob}
      rfq={rfq}
      renderSelectsMenu={renderSelectsMenu}
      valid={valid}
      invalidParams={invalidParams}
      loading={loading}
      showDownloadMenuIcon={showDownloadMenuIcon}
      jobsLoading={jobsLoading}
      errorExisted={errorExisted}
      skipDownloading={skipDownloading}
      productChanged={productChanged}
      resultFileJobId={resultFileJobId as string}
      selectedProduct={selectedProduct as string}
      resultFileNameRef={resultFileNameRef as string}
      quotePdfUrl={quotePdfUrl as string}
      quote={quote}
    />
  );

  useEffect(() => {
    if (queryParamsSideMenu?.product) setCurrentTab(ConfiguratorTab.CONFIGURE);
  }, [queryParamsSideMenu?.product]);

  const handleTabChange = (tab: ConfiguratorTab) => setCurrentTab(tab);

  const updateSelectedProduct = async (
    newSelectedProduct: number | string | undefined,
    currentProducts?: IProductResource[],
    currentUpdateViewer?: boolean | undefined
  ) => {
    if (currentUpdateViewer === undefined) {
      // eslint-disable-next-line no-param-reassign
      currentUpdateViewer = true;
    }

    const currentProduct = currentProducts
      ? currentProducts[newSelectedProduct as number]
      : products?.[newSelectedProduct as number];
    if (!currentProduct || Object.keys(currentProduct).length === 0) {
      return null;
    }

    const currentState = {
      selectedProduct: newSelectedProduct as number,
      product: currentProduct,
      parameterData: currentProduct.parameters,
      currentUpdateViewer,
    };

    handleUpdateSelectedProductStates(currentState);

    setInitialParams(currentProduct.parameters as IParameter[], currentProduct);

    return currentProduct;
  };

  const getItems = async () => {
    const productsResult = await (queryParamsProductMenu?.product
      ? fetchProductLink(queryParamsProductMenu.product as string)
      : fetchProducts());

    if (checkIfError(productsResult)) return;

    const newProducts = (
      productsResult as {
        products: IProductResource[];
      }
    ).products ?? [productsResult];

    const currentState = {
      newProducts: newProducts ?? null,
    };

    handleGetItemState(currentState);

    let newCurrentProduct;
    if (!queryParamsProductMenu?.token && !queryParamsProductMenu?.product) {
      const keys = Object.keys(newProducts);
      newCurrentProduct = newProducts[keys[0] as unknown as number];
      setInitialParams(newCurrentProduct?.parameters ?? null, newCurrentProduct);

      return;
    }

    const productKey = newProducts.findIndex(({ name }) => name === queryParamsProductMenu.product);

    newCurrentProduct = updateSelectedProduct(productKey.toString(), newProducts, true);
  };

  useEffect(() => {
    getItems();
  }, []);

  const renderProductMenu = (
    <ProductMenu
      products={products as IProductResource[]}
      selectedProduct={selectedProduct}
      queryParams={queryParamsProductMenu}
      loading={loading}
      viewerLoading={viewerLoading}
      updateSelectedProduct={updateSelectedProduct}
    />
  );

  const productsTab = !queryParamsSideMenu?.product
    ? [
        {
          id: ConfiguratorTab.PRODUCTS,
          label: 'Products',
          tabComponent: <Icon as={FaBoxes} size="lg" />,
          panel: !closed ? (
            <CollapsibleHalf version={1} title="Products" subtitle={theme?.companyName}>
              {renderProductMenu}
            </CollapsibleHalf>
          ) : (
            <Box />
          ),
        },
      ]
    : [];

  const downloadsTab =
    DownloadMenuIcon && (paramsOptions === viewParameters || skipDownloading)
      ? [
          {
            id: ConfiguratorTab.DOWNLOADS,
            label: 'Review',
            tabComponent: <Icon as={FaCloudDownloadAlt} size="lg" />,
            panel: !closed ? (
              <Box
                bg="linear-gradient(
                300deg,
                var(--chakra-colors-base1Colour-400) 0%,
                var(--chakra-colors-base1Colour-500) 80%,
                var(--chakra-colors-base1Colour-500) 100%
              )"
                h="full"
              >
                <CollapsibleHalf
                  title="Specifications"
                  subtitle={productDisplayName}
                  titleProps={{
                    color: 'secondaryThemeColour.400',
                  }}
                  subtitleProps={{
                    color: 'secondaryFontColour.400',
                  }}
                >
                  {cloneElement(PostProcessMenu, {
                    onTabChange: handleTabChange,
                  })}
                </CollapsibleHalf>
              </Box>
            ) : (
              <Box />
            ),
          },
        ]
      : [];

  const tabOptions = [
    ...productsTab,
    {
      id: ConfiguratorTab.CONFIGURE,
      label: 'Configure',
      tabComponent: <Icon as={FaSlidersH} size="lg" />,
      panel: !closed ? (
        <CollapsibleHalf
          title="Configuration"
          subtitle={theme?.hideProductNames === 'true' ? null : productDisplayName}
          version={1}
        >
          {cloneElement(OptionMenu, {
            onTabChange: handleTabChange,
          })}
        </CollapsibleHalf>
      ) : (
        <Box />
      ),
    },
    ...downloadsTab,
  ];

  const toggleTitle = tabOptions.find(({ id }) => id === currentTab)?.label;

  const renderAccessOverlay = !isEnabled ? <AccessOverlay /> : null;

  return (
    <Box>
      {renderAccessOverlay}

      <Flex pt={0}>
        <SideMenuTabs
          onChangeTab={handleTabChange}
          options={tabOptions}
          currentTab={currentTab}
          panelProps={{
            h: queryParamsSideMenu?.product ? '100vh' : 'calc(100vh - 82px)',
          }}
        />
        <SideMenuToggle
          title={toggleTitle}
          closed={closed}
          onToggleSideMenu={handleToggleSideMenu}
          bg={
            currentTab === ConfiguratorTab.DOWNLOADS
              ? `linear-gradient(
156deg,
var(--chakra-colors-secondaryThemeColour-400) 0%,
var(--chakra-colors-secondaryThemeColour-500) 100%
)`
              : primaryThemeGradient
          }
          _hover={{
            bg:
              currentTab === ConfiguratorTab.DOWNLOADS
                ? `linear-gradient(
156deg,
var(--chakra-colors-secondaryThemeColour-400) 0%,
var(--chakra-colors-secondaryThemeColour-500) 100%
)`
                : primaryThemeGradientDark,
          }}
        />
      </Flex>
    </Box>
  );
};

export default SideMenu;
